import { useEffect, useRef, useState } from 'react';
import { tv } from 'tailwind-variants';

import { Icon } from '@frontendmentorio/design-system-v2';

const styles = tv({
  base: [
    'auto-cols-[80%]',
    'gap-5',
    'grid',
    'grid-flow-col',
    'overflow-x-auto',
    'overscroll-x-contain',
    'row-end-3',
    'row-start-2',
    '[scrollbar-width:none]',
    'snap-x',
    'snap-mandatory',
    'w-full',
    '[&::-webkit-scrollbar]:hidden',
    'tablet:auto-cols-[calc(50%-theme(spacing[5]))]',
    'desktop:auto-cols-[calc(33%-calc(theme(spacing[5])/1.5))]',
    'tablet:col-span-2',
    'tablet:col-start-1',
  ],
});

export default function Carousel({ children }) {
  const ref = useRef();
  const [currentIdx, setCurrentIdx] = useState(0);
  const [lastIdx, setLastIdx] = useState(false);

  const initCarousel = () => {
    ref.current.scrollTo(0, 0);
    const itemCount = ref.current.children.length;
    const containerWidth = ref.current.offsetWidth;
    const itemWidth = ref.current.firstChild.offsetWidth;
    const itemsVisible = Math.floor(containerWidth / itemWidth);

    setCurrentIdx(0);
    setLastIdx(itemCount - itemsVisible);
  };

  const scrollToItem = (idx) => {
    const item = ref.current.children[idx];

    item.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start',
    });
  };

  const handleNext = async () => {
    await setCurrentIdx(currentIdx + 1);
    scrollToItem(currentIdx + 1);
  };

  const handlePrev = async () => {
    await setCurrentIdx(currentIdx - 1);
    scrollToItem(currentIdx - 1);
  };

  useEffect(() => {
    initCarousel();
    window.addEventListener('resize', initCarousel);

    return () => window.removeEventListener('resize', initCarousel);
  }, []);

  return (
    <>
      <div className="flex gap-3 items-center tablet:col-start-2 tablet:col-span-1">
        <button
          aria-label="Scroll to previous"
          className="aspect-square bg-blue-800 flex items-center justify-center rounded-full transition-colors w-[3.75rem] disabled:bg-neutral-500 hover:bg-blue-900"
          disabled={currentIdx === 0}
          onClick={handlePrev}
        >
          <Icon
            className="text-white text-xl"
            name="arrow-left"
            style="solid"
          />
        </button>

        <button
          aria-label="Scroll to next"
          className="aspect-square bg-blue-800 flex items-center justify-center rounded-full transition-colors w-[3.75rem] disabled:bg-neutral-500 hover:bg-blue-900"
          disabled={currentIdx === lastIdx}
          onClick={handleNext}
        >
          <Icon
            className="text-white text-xl"
            name="arrow-right"
            style="solid"
          />
        </button>
      </div>

      <ul className={styles()} ref={ref}>
        {children}
      </ul>
    </>
  );
}
